import { GaugeMarkerColors } from 'src/constants/gaugeMarkerColors';


/**
 * Helper function to get the inundation height for a given gauge
 * @param height - the height to check
 * @param levels - the levels to check against
 * @param lockMin - whether or not to lock to the min height
 * @param lockMax - whether or not to lock to the max height
 * @returns 
 */
export const getInundationLevel = (height, levels, lockMin = false, lockMax = true) => {
  if (!levels || levels.length === 0) return height;

  const min = Math.min(...levels);
  const max = Math.max(...levels);

  if (height < min) {
    return lockMin ? min : height;
  }

  if (height > max) {
    return lockMax ? max : height;
  }

  // find the level equal to or just above the height
  const level = levels.find((level) => level >= height);

  return level;
}

/**
 * Helper function to get the inundation color for a given gauge
 * @param height - the height to check
 * @returns the color for the inundation level
 */
const getConditionColor = (gauge, height) => {
  const { bankFull, minor, moderate, major, } = gauge;

  if (!bankFull) {
    return GaugeMarkerColors.GRAY;
  }

  let color = GaugeMarkerColors.GRAY;

  if (height < bankFull) {
    color = GaugeMarkerColors.GREEN;
  } else if (height < minor) {
    color = GaugeMarkerColors.YELLOW;
  } else if (height < moderate) {
    color = GaugeMarkerColors.ORANGE;
  } else if (height < major) {
    color = GaugeMarkerColors.RED;
  } else {
    color = GaugeMarkerColors.PURPLE;
  }

  return color;
}

/**
 * Helper function to get forecast range data for a given gauge
 * Coastal forecast data has a 1 hour interval and data is in MSL
 * @param gauge - the gauge
 * @param data - the data, MSL
*/
export const getCoastalForecastData = (gauge, data) => {
  // coastal forecast hour intervals
  let start = 0;
  let end = 1;
  const forecastData = [];

  // the last data point is in the past, so nothing to forecast
  if (data[data.length - 1].read > 0) {
    return null;
  }

  for (let i = data[0].update; i < data.length; i++) {
    forecastData.push({
      height: data[i].value,
      stage: data[i].value,
      date: data[i].at,
      range: {
        min: start,
        max: end,
        color: getConditionColor(gauge, data[i].value),
      }
    });

    start = end;
    end += 1;
  }

  return {
    forecastData,
    maxForecastTime: data.length - data[data.length - 1].update
  };
}

/**
 * Helper function to get forecast range data for a given gauge
 * River forecast data has a 6 hour interval and data is in MSL.
 * Uses the gauge datum to convert to stage.
 * @param gauge - the gauge
 * @param data - the data, MSL
*/
export const getRiverForecastData = (gauge, data) => {
  // river forecast 6 hour intervals
  let start = 0;
  let end = 6;
  const forecastData = [];

  // the last data point is in the past, so nothing to forecast
  if (data[data.length - 1].read > 0) {
    return null;
  }
    
  for (let i = Math.floor(data[0].update/6); i < data.length; i++) {
    forecastData.push({
      height: data[i].value,
      stage: data[i].value - gauge.gageDatum,
      date: data[i].at,
      range: {
        min: start,
        max: end,
        color: getConditionColor(gauge, data[i].value),
      }
    });

    start = end;
    end += 6;
  }

  return {
    forecastData,
    maxForecastTime: data.length * 6 - data[data.length - 1].update
  };
}

export const getNwmForecastData = (gauge, data) => {
  let start = 0;
  let end = 1;
  const forecastData = [];

  // the last data point is in the past, so nothing to forecast
  if (data[data.length - 1].read > 0) {
    return null;
  }

  for (let i = data[0].update; i < data.length; i++) {
    forecastData.push({
      height: data[i].value,
      stage: data[i].value,
      date: data[i].at,
      range: {
        min: start,
        max: end,
        color: getConditionColor(gauge, data[i].value + gauge.gageDatum),
      }
    });

    start = end;
    end += 1;
  }

  return {
    forecastData,
    maxForecastTime: data.length - data[data.length - 1].update
  };
}

